import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './LegalPage.css';

export default function TermsOfService() {
  return (
    <main className="legal-page" role="main">
      <Helmet>
        <title>Terms of Service | EffyVenture — Effy Tech Venture Private Limited</title>
        <meta name="description" content="Terms of Service of Effy Tech Venture Private Limited. Read the legally binding terms governing your use of the EffyVenture SaaS platform." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.effyventure.com/terms-of-service" />
      </Helmet>

      <div className="legal-page__hero">
        <div className="legal-page__hero-inner">
          <div className="legal-page__badge">Legal</div>
          <h1 className="legal-page__title">Terms of Service</h1>
          <p className="legal-page__meta">
            <strong>Effy Tech Venture Private Limited</strong>
            <span className="legal-page__meta-sep">|</span>
            Effective Date: 8 May 2026
            <span className="legal-page__meta-sep">|</span>
            Version 1.0
          </p>
        </div>
      </div>

      <div className="legal-page__body">
        <div className="legal-page__toc">
          <h2 className="legal-page__toc-title">Table of Contents</h2>
          <ol className="legal-page__toc-list">
            <li><a href="#definitions">Definitions</a></li>
            <li><a href="#acceptance">Acceptance of Terms</a></li>
            <li><a href="#description">Description of Service</a></li>
            <li><a href="#registration">User Registration &amp; Account</a></li>
            <li><a href="#subscriptions">Subscriptions, Fees &amp; Payment</a></li>
            <li><a href="#acceptable-use">Acceptable Use Policy</a></li>
            <li><a href="#user-data">User Data &amp; Content</a></li>
            <li><a href="#ip">Intellectual Property</a></li>
            <li><a href="#confidentiality">Confidentiality</a></li>
            <li><a href="#availability">Service Availability &amp; Support</a></li>
            <li><a href="#security">Security Obligations</a></li>
            <li><a href="#third-party">Third-Party Services &amp; Integrations</a></li>
            <li><a href="#warranties">Warranties &amp; Disclaimers</a></li>
            <li><a href="#liability">Limitation of Liability</a></li>
            <li><a href="#indemnification">Indemnification</a></li>
            <li><a href="#termination">Term &amp; Termination</a></li>
            <li><a href="#force-majeure">Force Majeure</a></li>
            <li><a href="#governing-law">Governing Law &amp; Dispute Resolution</a></li>
            <li><a href="#general">General Provisions</a></li>
            <li><a href="#contact">Contact Information</a></li>
          </ol>
        </div>

        <div className="legal-page__content">

          <div className="legal-page__important-notice">
            <strong>Important — Please Read Carefully</strong>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Effy Tech
              Venture Private Limited. By registering for, accessing, or using the Service in any form — whether
              through our cloud platform, on-premises deployment at your premises, or on-premises deployment at
              our premises — you agree to be bound by these Terms.
            </p>
            <p>
              If you are accepting these Terms on behalf of a company or other legal entity, you represent that
              you have the authority to bind that entity. If you do not agree to these Terms, do not access or
              use the Service.
            </p>
          </div>

          {/* 1. Definitions */}
          <section id="definitions" className="legal-page__section">
            <h2 className="legal-page__section-title">1. Definitions</h2>
            <p>In these Terms, the following words and expressions shall have the meanings assigned to them below:</p>
            <dl className="legal-page__dl">
              <dt>"Effy Tech Venture" / "Company" / "we" / "us" / "our"</dt>
              <dd>Effy Tech Venture Private Limited, a company incorporated under the Companies Act, 2013, with its registered office in Chennai, Tamil Nadu, India.</dd>

              <dt>"Service"</dt>
              <dd>The software platform provided by Effy Tech Venture for bookkeeping, billing, invoicing, GST compliance reporting, inventory management, P&amp;L analytics, and related features, delivered under any of the three deployment models: Cloud, On-Premises (User's site), or On-Premises (Company's site).</dd>

              <dt>"User" / "you" / "your"</dt>
              <dd>Any individual, proprietorship, partnership, LLP, private limited company, or any other legal entity that registers for or uses the Service.</dd>

              <dt>"Subscription"</dt>
              <dd>The paid or trial access plan under which the User accesses the Service, as described in the applicable Order Form or Plan page.</dd>

              <dt>"User Data"</dt>
              <dd>All financial records, invoices, transactions, inventory data, GST data, and other business information that the User inputs into or generates using the Service.</dd>

              <dt>"Intellectual Property"</dt>
              <dd>All patents, copyrights, trademarks, trade secrets, software code, algorithms, designs, and other proprietary rights owned by or licensed to Effy Tech Venture.</dd>

              <dt>"Confidential Information"</dt>
              <dd>Any non-public information disclosed by one party to the other in connection with the Service, including pricing, technical specifications, business processes, and User Data.</dd>

              <dt>"Order Form"</dt>
              <dd>A written or electronic document, quotation, or plan selection that specifies the Subscription tier, deployment model, fees, and any additional terms agreed between the parties.</dd>

              <dt>"Effective Date"</dt>
              <dd>8 May 2026, or the date on which the User first accesses or uses the Service, whichever is earlier.</dd>
            </dl>
          </section>

          {/* 2. Acceptance */}
          <section id="acceptance" className="legal-page__section">
            <h2 className="legal-page__section-title">2. Acceptance of Terms</h2>
            <p>
              By clicking "I Agree", "Sign Up", or any equivalent button or checkbox, by executing an Order Form,
              or by accessing or using the Service in any manner, you confirm that:
            </p>
            <ul className="legal-page__list">
              <li>You have read, understood, and agree to be bound by these Terms and our <Link to="/privacy-policy">Privacy Policy</Link> (available on our website), which is incorporated herein by reference.</li>
              <li>You are at least 18 years of age and have the legal capacity to enter into a binding contract under Indian law.</li>
              <li>If accepting on behalf of an entity, you have full authority to bind that entity to these Terms.</li>
              <li>You will comply with all applicable laws, rules, and regulations, including the Goods and Services Tax (GST) laws, the Income Tax Act, 1961, the Companies Act, 2013, and the Information Technology Act, 2000.</li>
            </ul>
          </section>

          {/* 3. Description */}
          <section id="description" className="legal-page__section">
            <h2 className="legal-page__section-title">3. Description of Service</h2>
            <p>Effy Tech Venture provides a comprehensive business management software platform with the following core features:</p>
            <ul className="legal-page__list">
              <li><strong>Bookkeeping &amp; Accounting</strong> — Double-entry accounting, journal entries, ledger management, and financial statements.</li>
              <li><strong>Billing &amp; Invoicing</strong> — Creation, management, and tracking of sales invoices, purchase bills, credit notes, and debit notes.</li>
              <li><strong>GST Compliance</strong> — GSTR-1, GSTR-3B, GSTR-9 preparation and e-filing support; e-invoicing (IRN/QR) and e-way bill generation via NIC integration.</li>
              <li><strong>Inventory Management</strong> — Stock tracking, purchase and sales order management, product master data, and valuation.</li>
              <li><strong>Financial Reporting</strong> — Profit &amp; Loss statements, Balance Sheets, Cash Flow statements, and customisable MIS reports.</li>
              <li><strong>User &amp; Role Management</strong> — Multi-user access with configurable roles and permissions.</li>
            </ul>

            <h3 className="legal-page__subsection-title">3.1 Deployment Models</h3>
            <p>The Service is available under the following deployment configurations, as agreed in the applicable Order Form:</p>
            <ul className="legal-page__list">
              <li><strong>(a) Cloud Deployment:</strong> The Service and all User Data are hosted on servers managed by Effy Tech Venture or its authorised infrastructure partners, located within India. Users access the Service via a web browser or authorised application over the internet.</li>
              <li><strong>(b) On-Premises Deployment — User's Site:</strong> The Service software is installed and operated on servers owned and maintained by the User at their own premises. The User is responsible for server hardware, operating system, network access, and physical security. Effy Tech Venture provides installation support, software updates, and remote maintenance access as agreed.</li>
              <li><strong>(c) On-Premises Deployment — Effy Tech Venture's Premises:</strong> The software and User Data are hosted on dedicated servers at Effy Tech Venture's facility in Chennai. Physical access is restricted to authorised Effy Tech Venture personnel only.</li>
            </ul>
            <p>
              Effy Tech Venture reserves the right to add, modify, suspend, or discontinue any feature of the
              Service with reasonable prior notice to Users.
            </p>
          </section>

          {/* 4. Registration */}
          <section id="registration" className="legal-page__section">
            <h2 className="legal-page__section-title">4. User Registration &amp; Account</h2>

            <h3 className="legal-page__subsection-title">4.1 Account Creation</h3>
            <p>
              To use the Service, you must register and create an account by providing accurate, complete, and
              up-to-date information including your name, business name, GSTIN (if applicable), email address,
              and mobile number. You agree to keep this information current at all times.
            </p>

            <h3 className="legal-page__subsection-title">4.2 Account Security</h3>
            <ul className="legal-page__list">
              <li>You are solely responsible for maintaining the confidentiality of your login credentials, including passwords and OTPs.</li>
              <li>You must enable multi-factor authentication (MFA) if offered and recommended for your account.</li>
              <li>You agree to notify Effy Tech Venture immediately upon becoming aware of any unauthorised access to your account.</li>
              <li>Effy Tech Venture shall not be liable for any loss or damage arising from your failure to safeguard your credentials.</li>
            </ul>

            <h3 className="legal-page__subsection-title">4.3 Account Types &amp; Authorised Users</h3>
            <p>
              The primary account holder ("Account Administrator") may add sub-users with defined roles and
              permissions. The Account Administrator is responsible for ensuring that all sub-users comply with
              these Terms. Any act or omission of a sub-user is deemed to be the act or omission of the Account
              Administrator.
            </p>

            <h3 className="legal-page__subsection-title">4.4 Business Verification</h3>
            <p>
              Effy Tech Venture reserves the right to verify the identity and registration details of any User
              at any time. Failure to provide satisfactory verification may result in suspension or termination
              of the account.
            </p>
          </section>

          {/* 5. Subscriptions */}
          <section id="subscriptions" className="legal-page__section">
            <h2 className="legal-page__section-title">5. Subscriptions, Fees &amp; Payment</h2>

            <h3 className="legal-page__subsection-title">5.1 Subscription Plans</h3>
            <p>
              Access to the Service is subject to a paid Subscription, the details of which (including features
              included, number of users, storage limits, deployment model, and fees) are specified in the
              applicable Order Form or Plan page. Effy Tech Venture may offer a free trial period, the terms of
              which will be communicated at the time of sign-up.
            </p>

            <h3 className="legal-page__subsection-title">5.2 Fees &amp; Taxes</h3>
            <ul className="legal-page__list">
              <li>All fees are stated in Indian Rupees (INR) and are exclusive of Goods and Services Tax (GST) and any other applicable taxes, levies, or duties.</li>
              <li>GST and other applicable taxes will be charged at the prevailing statutory rate and shall be payable by the User in addition to the stated fees.</li>
              <li>Effy Tech Venture will issue a GST-compliant tax invoice for all payments received.</li>
            </ul>

            <h3 className="legal-page__subsection-title">5.3 Payment Terms</h3>
            <ul className="legal-page__list">
              <li>Subscription fees are payable in advance on a monthly, quarterly, or annual basis as selected by the User.</li>
              <li>Payment must be made through the authorised payment methods available on the platform (credit/debit card, net banking, UPI, NEFT/RTGS, or cheque as applicable).</li>
              <li>For on-premises deployments, a separate one-time implementation or setup fee may apply as specified in the Order Form.</li>
            </ul>

            <h3 className="legal-page__subsection-title">5.4 Late Payment &amp; Suspension</h3>
            <p>
              If any undisputed payment is not received within 15 days of the due date, Effy Tech Venture
              reserves the right to suspend access to the Service. Service will be reinstated within 2 business
              days of receipt of the overdue amount, subject to any applicable reconnection fee.
            </p>

            <h3 className="legal-page__subsection-title">5.5 Refund Policy</h3>
            <p>Subscription fees paid in advance are non-refundable, except in the following circumstances:</p>
            <ul className="legal-page__list">
              <li>Where Effy Tech Venture is unable to deliver the Service due to reasons solely attributable to the Company and such failure continues for more than 15 consecutive days.</li>
              <li>Where a refund is expressly agreed in writing in the applicable Order Form.</li>
            </ul>
            <p>Requests for refunds must be submitted in writing to <a href="mailto:billing@effytech.in">billing@effytech.in</a> within 30 days of the billing date.</p>

            <h3 className="legal-page__subsection-title">5.6 Price Changes</h3>
            <p>
              Effy Tech Venture reserves the right to revise Subscription fees at any time with at least
              <strong> 30 days' prior written notice</strong> to existing Users. Continued use of the Service
              after the revised fees take effect constitutes acceptance of the new pricing.
            </p>
          </section>

          {/* 6. Acceptable Use */}
          <section id="acceptable-use" className="legal-page__section">
            <h2 className="legal-page__section-title">6. Acceptable Use Policy</h2>

            <h3 className="legal-page__subsection-title">6.1 Permitted Use</h3>
            <p>
              The Service is licensed solely for your internal business operations. You may use the Service to
              manage your own business's accounting, billing, invoicing, GST compliance, and inventory. You
              must not resell, sublicense, or provide the Service to third parties as a bureau service without
              a separate written agreement with Effy Tech Venture.
            </p>

            <h3 className="legal-page__subsection-title">6.2 Prohibited Use</h3>
            <p>You agree that you will not, directly or indirectly:</p>
            <ul className="legal-page__list">
              <li>Use the Service for any unlawful purpose or in violation of any applicable law or regulation.</li>
              <li>Input, upload, or transmit any data that is fraudulent, falsified, or designed to evade tax obligations.</li>
              <li>Attempt to gain unauthorised access to any part of the Service, its servers, or any connected system.</li>
              <li>Reverse-engineer, decompile, disassemble, or attempt to derive the source code of the Service.</li>
              <li>Copy, reproduce, modify, distribute, or create derivative works of the Service or any part thereof.</li>
              <li>Use the Service to transmit spam, malware, or any harmful or disruptive code.</li>
              <li>Interfere with or disrupt the integrity, performance, or availability of the Service.</li>
              <li>Use automated bots, scrapers, or crawlers to extract data from the Service.</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
              <li>Use the Service in any manner that could harm Effy Tech Venture's reputation or goodwill.</li>
            </ul>

            <h3 className="legal-page__subsection-title">6.3 Compliance with GST &amp; Tax Laws</h3>
            <p>
              The Service is designed to assist with GST compliance; however, the User is solely responsible
              for the accuracy, completeness, and timeliness of all tax filings made using the Service. Effy
              Tech Venture does not provide tax advisory services. You should consult a qualified Chartered
              Accountant or tax professional for advice specific to your business.
            </p>
          </section>

          {/* 7. User Data */}
          <section id="user-data" className="legal-page__section">
            <h2 className="legal-page__section-title">7. User Data &amp; Content</h2>

            <h3 className="legal-page__subsection-title">7.1 Ownership</h3>
            <p>
              As between the parties, you retain full ownership of all User Data that you input into or generate
              using the Service. Effy Tech Venture claims no ownership rights over your User Data.
            </p>

            <h3 className="legal-page__subsection-title">7.2 Licence to Effy Tech Venture</h3>
            <p>
              You grant Effy Tech Venture a limited, non-exclusive, royalty-free licence to access, process,
              store, and use your User Data solely for the purpose of providing, maintaining, and improving the
              Service, and as described in our <Link to="/privacy-policy">Privacy Policy</Link>. This licence
              terminates upon deletion of your data or termination of your account.
            </p>

            <h3 className="legal-page__subsection-title">7.3 User Responsibilities</h3>
            <ul className="legal-page__list">
              <li>You are solely responsible for the accuracy, legality, and completeness of all User Data.</li>
              <li>You must ensure that you have all necessary rights and permissions to input third-party data (e.g., customer or vendor information) into the Service.</li>
              <li>You are responsible for maintaining your own backup copies of critical data, particularly for on-premises deployments where Effy Tech Venture does not manage the server infrastructure.</li>
            </ul>

            <h3 className="legal-page__subsection-title">7.4 Data Backup</h3>
            <p>
              For Cloud Deployment, Effy Tech Venture performs regular automated backups of User Data. For
              On-Premises Deployments (User's Site), the User is solely responsible for implementing and
              maintaining an appropriate data backup and disaster recovery strategy. For On-Premises Deployments
              at Effy Tech Venture's Premises, backup procedures are as specified in the Order Form.
            </p>

            <h3 className="legal-page__subsection-title">7.5 Data Export &amp; Portability</h3>
            <p>
              Upon written request, Effy Tech Venture will provide the User with an export of their User Data
              in a standard machine-readable format (CSV, Excel, or equivalent) within <strong>15 business days</strong>.
              One data export per subscription year is provided at no charge; additional exports may be subject
              to a nominal administrative fee.
            </p>
          </section>

          {/* 8. IP */}
          <section id="ip" className="legal-page__section">
            <h2 className="legal-page__section-title">8. Intellectual Property</h2>

            <h3 className="legal-page__subsection-title">8.1 Effy Tech Venture's IP</h3>
            <p>
              The Service, including all software, source code, algorithms, user interfaces, designs, databases,
              documentation, and branding, is the exclusive intellectual property of Effy Tech Venture Private
              Limited and is protected under the Copyright Act, 1957; the Patents Act, 1970; the Trade Marks
              Act, 1999; and other applicable Indian and international intellectual property laws. No rights in
              the Company's Intellectual Property are transferred to the User under these Terms.
            </p>

            <h3 className="legal-page__subsection-title">8.2 Licence to User</h3>
            <p>
              Subject to your compliance with these Terms and timely payment of Subscription fees, Effy Tech
              Venture grants you a limited, non-exclusive, non-transferable, revocable licence to access and
              use the Service solely for your internal business purposes during the Subscription term.
            </p>

            <h3 className="legal-page__subsection-title">8.3 Feedback</h3>
            <p>
              If you provide suggestions, ideas, enhancement requests, or other feedback about the Service
              ("Feedback"), you grant Effy Tech Venture a perpetual, irrevocable, royalty-free licence to use,
              incorporate, and commercialise such Feedback without any obligation or compensation to you.
            </p>
          </section>

          {/* 9. Confidentiality */}
          <section id="confidentiality" className="legal-page__section">
            <h2 className="legal-page__section-title">9. Confidentiality</h2>
            <p>
              Each party agrees to keep the other party's Confidential Information strictly confidential and
              not to disclose it to any third party without prior written consent, except as required by law
              or as necessary to perform obligations under these Terms. Each party agrees to use the other
              party's Confidential Information solely for the purposes contemplated by these Terms.
            </p>
            <p>
              Confidentiality obligations shall survive the termination of these Terms for a period of
              <strong> 3 years</strong>, or for as long as the information remains confidential by nature,
              whichever is longer.
            </p>
          </section>

          {/* 10. Availability */}
          <section id="availability" className="legal-page__section">
            <h2 className="legal-page__section-title">10. Service Availability &amp; Support</h2>

            <h3 className="legal-page__subsection-title">10.1 Uptime Commitment (Cloud Deployment)</h3>
            <p>
              For Cloud Deployment Users, Effy Tech Venture aims to maintain a monthly uptime of
              <strong> 99%</strong> for the Service, excluding scheduled maintenance windows. Scheduled
              maintenance will be communicated at least 48 hours in advance via email or in-app notification
              and will be scheduled during off-peak hours wherever practicable.
            </p>

            <h3 className="legal-page__subsection-title">10.2 On-Premises Availability</h3>
            <p>
              For On-Premises Deployments, service availability depends on the User's own infrastructure (for
              User's site deployments) or on Effy Tech Venture's internal infrastructure (for Company's site
              deployments). Uptime commitments for on-premises deployments at the Company's premises will be
              specified in the applicable Order Form.
            </p>

            <h3 className="legal-page__subsection-title">10.3 Technical Support</h3>
            <p>
              Technical support is available to all active Subscription holders during business hours (Monday
              to Friday, 10:00 AM to 6:00 PM IST, excluding public holidays) via:
            </p>
            <ul className="legal-page__list">
              <li>Email: <a href="mailto:support@effytech.in">support@effytech.in</a></li>
              <li>In-app support chat (Cloud Deployment)</li>
              <li>Telephone support (as specified in the Order Form for on-premises plans)</li>
            </ul>

            <h3 className="legal-page__subsection-title">10.4 Software Updates</h3>
            <ul className="legal-page__list">
              <li><strong>Cloud Deployment:</strong> Updates are applied automatically by Effy Tech Venture. Users will be notified of significant updates in advance.</li>
              <li><strong>On-Premises Deployment:</strong> Software updates and patches are made available periodically. Installation is the responsibility of the User (for User's site) or Effy Tech Venture (for Company's site), as agreed in the Order Form. Users on outdated versions are encouraged to upgrade promptly to maintain security and GST compliance.</li>
            </ul>
          </section>

          {/* 11. Security */}
          <section id="security" className="legal-page__section">
            <h2 className="legal-page__section-title">11. Security Obligations</h2>

            <h3 className="legal-page__subsection-title">11.1 Effy Tech Venture's Obligations</h3>
            <p>
              Effy Tech Venture will implement and maintain reasonable technical and organisational security
              measures to protect User Data processed through the Service, as further described in our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>. These include encryption, access controls, and
              regular security assessments.
            </p>

            <h3 className="legal-page__subsection-title">11.2 User's Obligations</h3>
            <ul className="legal-page__list">
              <li>You are responsible for the security of your login credentials, API keys, and any devices used to access the Service.</li>
              <li>For On-Premises Deployments at your site, you are solely responsible for the physical security of servers, network security, firewall configurations, and operating system patching.</li>
              <li>You must report any suspected security incident or data breach to <a href="mailto:security@effytech.in">security@effytech.in</a> immediately.</li>
              <li>You must not attempt to circumvent, disable, or interfere with any security feature of the Service.</li>
            </ul>
          </section>

          {/* 12. Third-Party */}
          <section id="third-party" className="legal-page__section">
            <h2 className="legal-page__section-title">12. Third-Party Services &amp; Integrations</h2>
            <p>
              The Service may integrate with or provide links to third-party services and government portals
              including the GSTN portal, the National Informatics Centre (NIC) e-invoice system, banking APIs,
              payment gateways, SMS/email providers, and the <strong>Meta WhatsApp Business API</strong> (used
              to deliver transactional order and invoice notifications to end customers). Effy Tech Venture does
              not control these third-party services and is not responsible for their availability, accuracy,
              or content.
            </p>
            <p>
              Your use of third-party integrations is governed by the respective terms and privacy policies of
              those third parties. Effy Tech Venture shall not be liable for any loss or damage arising from
              your use of, or reliance on, any third-party service or integration.
            </p>
          </section>

          {/* 13. Warranties */}
          <section id="warranties" className="legal-page__section">
            <h2 className="legal-page__section-title">13. Warranties &amp; Disclaimers</h2>

            <h3 className="legal-page__subsection-title">13.1 Effy Tech Venture's Warranties</h3>
            <p>Effy Tech Venture warrants that:</p>
            <ul className="legal-page__list">
              <li>The Service will substantially perform the functions described in the Service documentation during the Subscription term.</li>
              <li>It has the right and authority to grant the licence set out in these Terms.</li>
              <li>It will comply with all applicable Indian laws in providing the Service.</li>
            </ul>

            <h3 className="legal-page__subsection-title">13.2 Disclaimers</h3>
            <p className="legal-page__disclaimer">
              EXCEPT AS EXPRESSLY SET OUT IN CLAUSE 13.1, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE"
              WITHOUT ANY WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. EFFY TECH VENTURE
              SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, NON-INFRINGEMENT, AND ACCURACY.
            </p>
            <p>In particular:</p>
            <ul className="legal-page__list">
              <li>Effy Tech Venture does not warrant that the Service will be error-free, uninterrupted, or free from bugs or security vulnerabilities.</li>
              <li>Effy Tech Venture does not warrant that GST returns or tax computations generated by the Service are free from errors. The User is solely responsible for reviewing and verifying all outputs before submission to any government authority.</li>
              <li>The Service is a software tool and does not constitute legal, tax, financial, or accounting advice.</li>
            </ul>
          </section>

          {/* 14. Liability */}
          <section id="liability" className="legal-page__section">
            <h2 className="legal-page__section-title">14. Limitation of Liability</h2>
            <p>To the maximum extent permitted by applicable law:</p>
            <ul className="legal-page__list">
              <li>Effy Tech Venture's total aggregate liability to the User for any and all claims arising out of or related to these Terms or the Service shall not exceed the total Subscription fees paid by the User to Effy Tech Venture in the <strong>12 months immediately preceding</strong> the event giving rise to the claim.</li>
              <li>In no event shall Effy Tech Venture be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, loss of data, loss of goodwill, business interruption, or cost of substitute services, even if Effy Tech Venture has been advised of the possibility of such damages.</li>
              <li>Effy Tech Venture shall not be liable for any loss or damage arising from: (a) the User's failure to maintain adequate backups (on-premises deployments); (b) errors in User-provided data; (c) non-compliance with GST or tax laws; (d) acts or omissions of third-party service providers; or (e) events beyond Effy Tech Venture's reasonable control (force majeure).</li>
              <li>Nothing in these Terms shall exclude or limit liability for fraud, death, or personal injury caused by negligence, or any other liability that cannot be excluded under applicable Indian law.</li>
            </ul>
          </section>

          {/* 15. Indemnification */}
          <section id="indemnification" className="legal-page__section">
            <h2 className="legal-page__section-title">15. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Effy Tech Venture Private Limited, its
              directors, officers, employees, and agents from and against any and all claims, damages, losses,
              liabilities, costs, and expenses (including reasonable legal fees) arising out of or relating to:
            </p>
            <ul className="legal-page__list">
              <li>Your use of the Service in violation of these Terms or applicable law.</li>
              <li>Any User Data that infringes the intellectual property, privacy, or other rights of any third party.</li>
              <li>Any fraudulent, misleading, or unlawful activity conducted through your account.</li>
              <li>Your failure to fulfil your obligations under GST, income tax, or any other applicable statute.</li>
            </ul>
          </section>

          {/* 16. Termination */}
          <section id="termination" className="legal-page__section">
            <h2 className="legal-page__section-title">16. Term &amp; Termination</h2>

            <h3 className="legal-page__subsection-title">16.1 Term</h3>
            <p>
              These Terms commence on the Effective Date and remain in force for the duration of your
              Subscription unless terminated earlier in accordance with this clause.
            </p>

            <h3 className="legal-page__subsection-title">16.2 Termination by User</h3>
            <p>
              You may terminate your Subscription at any time by providing <strong>30 days' written notice</strong> to{' '}
              <a href="mailto:support@effytech.in">support@effytech.in</a>. Termination will take effect at
              the end of the then-current billing cycle. Pre-paid fees for unused periods are non-refundable,
              except as provided in Clause 5.5.
            </p>

            <h3 className="legal-page__subsection-title">16.3 Termination by Effy Tech Venture</h3>
            <p>Effy Tech Venture may terminate or suspend your access to the Service immediately upon written notice if:</p>
            <ul className="legal-page__list">
              <li>You materially breach these Terms and fail to cure such breach within 15 days of written notice.</li>
              <li>You fail to pay any Subscription fees when due and do not remedy the default within 15 days.</li>
              <li>You become insolvent, are subject to bankruptcy proceedings, or cease to carry on business.</li>
              <li>Effy Tech Venture is required to do so by applicable law or a competent government authority.</li>
            </ul>
            <p>
              Effy Tech Venture may also terminate the Service with <strong>60 days' notice</strong> for any
              other reason, in which case a pro-rata refund of prepaid fees for the unused period will be provided.
            </p>

            <h3 className="legal-page__subsection-title">16.4 Effect of Termination</h3>
            <p>Upon termination for any reason:</p>
            <ul className="legal-page__list">
              <li>Your licence to use the Service ceases immediately.</li>
              <li>You must discontinue all use of the Service and delete any locally installed software components.</li>
              <li>For Cloud Deployments, you may request a data export within 30 days of termination. After this period, Effy Tech Venture will delete your User Data in accordance with the Privacy Policy.</li>
              <li>For On-Premises Deployments, you must return or destroy all software installation media and documentation provided by Effy Tech Venture.</li>
              <li>Provisions of these Terms that by their nature should survive termination (including Clauses 7.1, 8, 9, 13, 14, 15, 17, 18, and 19) shall continue in full force and effect.</li>
            </ul>
          </section>

          {/* 17. Force Majeure */}
          <section id="force-majeure" className="legal-page__section">
            <h2 className="legal-page__section-title">17. Force Majeure</h2>
            <p>
              Effy Tech Venture shall not be liable for any delay or failure in performance of the Service to
              the extent that such delay or failure is caused by circumstances beyond its reasonable control,
              including but not limited to acts of God, natural disasters, floods, fire, earthquakes, epidemics
              or pandemics, acts of war or terrorism, civil unrest, government actions, internet or
              telecommunications failures, power outages, or third-party infrastructure failures.
            </p>
            <p>
              Effy Tech Venture will notify the User as soon as practicable of any force majeure event and
              will use commercially reasonable efforts to resume performance as quickly as possible. If the
              force majeure event continues for more than <strong>30 days</strong>, either party may terminate
              the affected Subscription upon written notice, and a pro-rata refund of prepaid fees for
              undelivered service will be provided.
            </p>
          </section>

          {/* 18. Governing Law */}
          <section id="governing-law" className="legal-page__section">
            <h2 className="legal-page__section-title">18. Governing Law &amp; Dispute Resolution</h2>

            <h3 className="legal-page__subsection-title">18.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the Republic of
              India, without regard to conflict of law principles.
            </p>

            <h3 className="legal-page__subsection-title">18.2 Dispute Resolution</h3>
            <p>
              In the event of any dispute, controversy, or claim arising out of or relating to these Terms or
              the Service ("Dispute"), the parties shall first attempt to resolve the Dispute through
              good-faith negotiations within <strong>30 days</strong> of written notice from the aggrieved party.
            </p>
            <p>
              If the Dispute is not resolved through negotiation, it shall be referred to and finally resolved
              by arbitration under the Arbitration and Conciliation Act, 1996. The arbitration shall be
              conducted by a sole arbitrator mutually appointed by the parties, with the seat and venue of
              arbitration in Chennai, Tamil Nadu. The arbitration proceedings shall be conducted in the English
              language.
            </p>

            <h3 className="legal-page__subsection-title">18.3 Jurisdiction</h3>
            <p>
              Subject to the arbitration clause above, the parties submit to the exclusive jurisdiction of the
              courts located in <strong>Chennai, Tamil Nadu, India</strong> for any interim relief or for
              matters not subject to arbitration.
            </p>
          </section>

          {/* 19. General */}
          <section id="general" className="legal-page__section">
            <h2 className="legal-page__section-title">19. General Provisions</h2>
            <dl className="legal-page__dl">
              <dt>19.1 Entire Agreement</dt>
              <dd>These Terms, together with the Privacy Policy and any applicable Order Form, constitute the entire agreement between the parties with respect to the Service and supersede all prior or contemporaneous understandings, agreements, representations, and warranties.</dd>

              <dt>19.2 Amendments</dt>
              <dd>Effy Tech Venture may amend these Terms at any time by posting the revised Terms on its website or notifying Users via email at least 15 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the amended Terms.</dd>

              <dt>19.3 Assignment</dt>
              <dd>You may not assign or transfer your rights or obligations under these Terms without Effy Tech Venture's prior written consent. Effy Tech Venture may assign these Terms in connection with a merger, acquisition, or sale of all or substantially all of its assets, with notice to you.</dd>

              <dt>19.4 Severability</dt>
              <dd>If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force and effect.</dd>

              <dt>19.5 Waiver</dt>
              <dd>No failure or delay by either party in exercising any right under these Terms shall operate as a waiver of that right. A waiver of any breach shall not be deemed a waiver of any subsequent breach.</dd>

              <dt>19.6 Notices</dt>
              <dd>All legal notices under these Terms must be in writing and delivered by email with confirmation of receipt, or by registered post to the addresses specified in the applicable Order Form or as updated by either party in writing. Notices to Effy Tech Venture should be sent to <a href="mailto:legal@effytech.in">legal@effytech.in</a>.</dd>

              <dt>19.7 Relationship of Parties</dt>
              <dd>The parties are independent contractors. Nothing in these Terms shall be construed to create a partnership, joint venture, agency, franchise, employment, or fiduciary relationship between the parties.</dd>

              <dt>19.8 Language</dt>
              <dd>These Terms are drafted in the English language. In the event of any conflict between an English version and a translated version, the English version shall prevail.</dd>
            </dl>
          </section>

          {/* 20. Contact */}
          <section id="contact" className="legal-page__section">
            <h2 className="legal-page__section-title">20. Contact Information</h2>
            <p>
              For any questions, concerns, or notices relating to these Terms of Service, please contact us at:
            </p>
            <div className="legal-page__contact-card">
              <h3 className="legal-page__contact-title">Effy Tech Venture Private Limited</h3>
              <table className="legal-page__contact-table">
                <tbody>
                  <tr><th>Registered Office</th><td>E4 36, 4 46, D S Nagar Mannivakkam, Vandalur, Tambaram, Kanchipuram, Chennai, Tamil Nadu — 600048, India</td></tr>
                  <tr><th>CIN</th><td>U62013TN2026PTC192104</td></tr>
                  <tr><th>Email</th><td><a href="mailto:info@effyventure.com">info@effyventure.com</a></td></tr>
                  <tr><th>Phone</th><td><a href="tel:+919043395293">+91-9043395293</a></td></tr>
                  <tr><th>Working Hours</th><td>Monday to Friday, 10:00 AM – 6:00 PM IST</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <div className="legal-page__footer-note">
            <p>— End of Terms of Service —</p>
            <p>© Effy Tech Venture Private Limited. All rights reserved.</p>
            <p>
              <Link to="/privacy-policy">Privacy Policy</Link>
              {' '} | {' '}
              <Link to="/contact">Contact Us</Link>
            </p>
          </div>

        </div>
      </div>
    </main>
  );
}
